import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useBranding } from 'modules/App/hooks/useBranding';

// Brand-derived, not hardcoded: this modal opens from the PUBLIC portal page,
// which every whitelabel tenant's suppliers publish through — a literal
// "Carbon Glance" here puts CG's name and signup URL in a partner's customer's
// outbox. `${websiteUrl}/sign-up` reproduces CG's own link exactly for the CG
// build (its websiteUrl default is https://carbonglance.com) while following
// the tenant's site everywhere else.
const buildRequestEmail = (brand) => ({
  subject: `Sharing your emissions data with us — ${brand.name}`,
  body: `Hi,

We've started reporting CBAM-embedded emissions and need product emissions data from our suppliers. Rather than chase each one individually, we'd love it if you published your data via ${brand.name}.

With ${brand.name} you can prepare everything you need for EU CBAM verification:
• Monitoring plan, including a schematic diagram of your factory
• Operator's emissions report, including Specific Embedded Free Allocation (SEFA) values
• Operator's summary emissions report
• Supporting documentation

Sign up free: ${brand.websiteUrl}/sign-up

Once you're set up, send us your portal link and we'll pull the data directly.

Thanks!`,
});

/**
 * Conversion modal triggered by the header's "Get suppliers CBAM-ready" button.
 *
 * A copyable / mailto-ready email asking the visitor's *other* suppliers to
 * publish their emissions on the tenant's platform. The conversion path targets
 * the visitor's suppliers, not the visitor — declarants don't need to onboard
 * themselves to file CBAM.
 *
 * Split out of DownloadDataModal so a visitor who came to download a file isn't
 * routed through the pitch first (CG-2359).
 */
export default function RequestSupplierDataModal({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  const brand = useBranding();
  const { subject: REQUEST_EMAIL_SUBJECT, body: REQUEST_EMAIL_BODY } = buildRequestEmail(brand);

  const handleCopy = async () => {
    const fullEmail = `Subject: ${REQUEST_EMAIL_SUBJECT}\n\n${REQUEST_EMAIL_BODY}`;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullEmail);
      } else {
        // Fallback for older browsers / non-secure contexts.
        const ta = document.createElement('textarea');
        ta.value = fullEmail;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent fail — clipboard permissions can be denied in iframes/sandboxes;
      // user still has the Open-in-email-app path as a fallback.
    }
  };

  const handleOpenInMail = () => {
    const href = `mailto:?subject=${encodeURIComponent(
      REQUEST_EMAIL_SUBJECT,
    )}&body=${encodeURIComponent(REQUEST_EMAIL_BODY)}`;
    if (typeof window !== 'undefined') window.location.href = href;
  };

  return (
    <CgDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Get your suppliers CBAM-ready"
      description={`Ask your other suppliers to publish their emissions data on ${brand.name}.`}
      width={616}
    >
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-slate-700 m-0">
          Your declaration is only as defensible as your suppliers' data. Send this email to ask
          your suppliers to publish on {brand.name} — so they get verification-ready, and you get
          data you can rely on.
        </p>

        {/* Email preview */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
              Subject
            </div>
            <div className="text-xs text-slate-800 font-medium">{REQUEST_EMAIL_SUBJECT}</div>
          </div>
          <div className="px-3 py-3 max-h-[320px] overflow-y-auto">
            <pre className="text-xs leading-relaxed text-slate-700 whitespace-pre-wrap font-sans m-0">
              {REQUEST_EMAIL_BODY}
            </pre>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <CgRadixButton
            size="md"
            variant="secondary"
            onClick={handleCopy}
            className="whitespace-nowrap"
          >
            <FontAwesomeIcon
              icon={copied ? faCheck : faCopy}
              className="text-xs"
              style={copied ? { color: 'rgb(var(--primary-color))' } : undefined}
            />
            {copied ? 'Copied' : 'Copy email'}
          </CgRadixButton>
          <CgRadixButton
            size="md"
            variant="primary"
            onClick={handleOpenInMail}
            className="whitespace-nowrap"
          >
            <FontAwesomeIcon
              icon={faEnvelopeOpenText}
              className="text-xs"
            />
            Open in email app
          </CgRadixButton>
        </div>
      </div>
    </CgDialog>
  );
}
